/* eslint-disable react/prop-types */
/* eslint-disable react/no-array-index-key */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { Row as RTRow, Column } from "react-table";
import { useQueryClient } from "@tanstack/react-query";

import DataTable from "components/organisms/DataTable/DataTable";
import { getTasks } from "api";
import useTableRequest from "hooks/useTableRequest";
import moment from "moment";
import Badge from "components/atoms/Badge/Badge";
import { userQueryKeys } from "api/queries/user/userQueryKeys";
import GlobalTableSearch from "components/organisms/DataTable/components/GlobalTableSearch";
import PillTabs from "components/atoms/PillTabs/PillTabs";
import TaskDialog from "./components/TaskDialog";

const TASK_TYPES: any = {
  cost_recovery: "tasks.costRecovery",
  pending_approval_request: "tasks.pendingApprovalRequest",
  cancel_request: "tasks.cancelRequest",
};

function Tasks() {
  const [taskId, setTaskId] = useState(null);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const pageByStatusRef = useRef<Record<string, number>>({});

  const intl = useIntl();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const status: string = searchParams.get("status") || "";

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: userQueryKeys.userPendingTasks });
  }, []);

  const columns = useMemo<Column[]>(
    () => [
      {
        Header: intl.formatMessage({ id: "tasks.nameHeader", defaultMessage: "Name" }),
        accessor: "name",
        Cell: ({ value }) => value,
      },
      {
        Header: intl.formatMessage({ id: "tasks.shipment", defaultMessage: "Shipment" }),
        accessor: "shipment_id",
        Cell: ({ value }) => value,
      },
      {
        Header: intl.formatMessage({ id: "tasks.typeHeader", defaultMessage: "Notification Type" }),
        accessor: "type",
        Cell: ({ value }) => intl.formatMessage({ id: TASK_TYPES[value], defaultMessage: value }) || value,
      },
      {
        Header: intl.formatMessage({ id: "tasks.dateHeader", defaultMessage: "Date" }),
        accessor: "created_at",
        Cell: ({ value }) => moment(value).format("DD MMM YYYY - hh:mm"),
      },
      {
        Header: intl.formatMessage({
          id: "general.status",
          defaultMessage: "Status",
        }),
        accessor: "status",
        Cell: ({ value }) => {
          const label = value === "open" ? "Pending" : value === "closed" ? "Closed" : value;
          const type = value === "open" ? "danger" : "secondary";
          return (
            <div className="d-flex flex-column align-items-start">
              <Badge type={type} label={label} />
            </div>
          );
        },
      },
    ],
    [],
  );

  const { instance, fetching, totalData } = useTableRequest(getTasks, columns, {
    params: {
      status,
    },
    deps: [status],
    filters: (globalFilter) => ({
      ...(globalFilter.query?.length && { query: globalFilter.query }),
    }),
  });

  const handleRowClick = async (row: RTRow<any>) => {
    const type = row.original?.type;
    const shipmentId = row.original?.shipment_id;
    if (["pending_approval_request", "cancel_request"].includes(type) && shipmentId) {
      navigate(`/shipment/${shipmentId}`);
      return;
    }
    const currentTaskId = row.original?.id;
    setTaskId(currentTaskId);
    setShowTaskDialog(true);
  };

  const handleStatusChange = (eventKey: string | null) => {
    const currentStatus = status || "open";
    const currentPage = +(searchParams.get("page") || 1);
    pageByStatusRef.current[currentStatus] = currentPage;

    const nextStatus = eventKey || "open";
    const nextPage = pageByStatusRef.current[nextStatus] ?? 1;

    if (!eventKey) {
      searchParams.delete("status");
    } else {
      searchParams.set("status", eventKey);
    }
    searchParams.set("page", String(nextPage));
    setSearchParams(searchParams);
  };

  return (
    <>
      {taskId && (
        <TaskDialog
          isOpen={showTaskDialog}
          close={() => {
            setShowTaskDialog(false);
          }}
          taskId={taskId}
        />
      )}
      <Card body className="shadow-none quote-container">
        <div className="d-flex align-items-center mb-4">
          <h3 className="mr-3">
            <FormattedMessage id="general.tasks" defaultMessage="Tasks" />
          </h3>
          <GlobalTableSearch
            currentFilters={instance.state.globalFilter}
            setFilters={instance.setGlobalFilter}
            filterKey="query"
            eventContext="Resolution center list"
          />
        </div>
        <DataTable
          tableInstance={instance}
          totalData={totalData}
          isLoading={fetching}
          onRowClick={handleRowClick}
          withoutBody
          headerLeft={
            <PillTabs
              id="task-status"
              activeKey={status}
              onSelect={handleStatusChange}
              tabItems={[
                {
                  eventKey: "open",
                  title: intl.formatMessage({ id: "tabs.awaitingAction", defaultMessage: "Awaiting Action" }),
                },
                { eventKey: "done", title: intl.formatMessage({ id: "tabs.archived", defaultMessage: "Archived" }) },
              ]}
            />
          }
        />
      </Card>
    </>
  );
}

export default Tasks;
